/**
 * WhatsApp Flows component catalog — the single source of truth for which
 * Flow JSON components the platform supports, the props each exposes, and the
 * Meta-enforced constraints on them.
 *
 * Consumed by:
 *   • apps/api  — flow-validator.ts (pre-flight validation before push to Meta)
 *   • apps/web  — the visual form builder (component palette + properties panel)
 *
 * Pure data + types (no deps) so it ships in @leadsync/shared-types to both
 * client and server. Property names are the exact kebab-case keys Meta expects
 * in Flow JSON. Source: developers.facebook.com/docs/whatsapp/flows/reference/components
 */
/** Latest Flow JSON schema version Meta accepts. */
export const LATEST_FLOW_JSON_VERSION = '7.3';
/** Meta cap: a single screen may hold at most this many children. */
export const MAX_CHILDREN_PER_SCREEN = 50;
export const FLOW_COMPONENTS = [
    // ── Display ──────────────────────────────────────────────────────────────
    {
        type: 'TextHeading',
        label: 'Heading',
        group: 'display',
        capturesInput: false,
        sinceVersion: '1.0',
        props: [{ name: 'text', required: true, hint: 'Heading text (max 80 chars)' }],
    },
    {
        type: 'TextSubheading',
        label: 'Subheading',
        group: 'display',
        capturesInput: false,
        sinceVersion: '1.0',
        props: [{ name: 'text', required: true, hint: 'Subheading text (max 80 chars)' }],
    },
    {
        type: 'TextBody',
        label: 'Body text',
        group: 'display',
        capturesInput: false,
        sinceVersion: '1.0',
        props: [
            { name: 'text', required: true, hint: 'Body text (max 4096 chars)' },
            { name: 'markdown', hint: 'Enable markdown rendering' },
        ],
    },
    {
        type: 'TextCaption',
        label: 'Caption',
        group: 'display',
        capturesInput: false,
        sinceVersion: '1.0',
        props: [{ name: 'text', required: true, hint: 'Caption text (max 4096 chars)' }],
    },
    {
        type: 'RichText',
        label: 'Rich text',
        group: 'display',
        capturesInput: false,
        sinceVersion: '5.1',
        props: [
            { name: 'text', required: true, hint: 'Markdown string or array of lines' },
            { name: 'visible' },
        ],
    },
    {
        type: 'Image',
        label: 'Image',
        group: 'display',
        capturesInput: false,
        sinceVersion: '1.0',
        props: [
            { name: 'src', required: true, hint: 'Base64 data or resource id' },
            { name: 'scale-type', hint: 'contain | cover' },
            { name: 'aspect-ratio' },
            { name: 'width' },
            { name: 'height' },
        ],
    },
    // ── Text input ───────────────────────────────────────────────────────────
    {
        type: 'TextInput',
        label: 'Text input',
        group: 'input',
        capturesInput: true,
        sinceVersion: '1.0',
        inputTypes: ['text', 'number', 'email', 'password', 'passcode', 'phone'],
        props: [
            { name: 'name', required: true },
            { name: 'label', required: true, hint: 'Field label (max 20 chars)' },
            { name: 'input-type', hint: 'text | number | email | password | passcode | phone' },
            { name: 'required' },
            { name: 'helper-text' },
            { name: 'min-chars' },
            { name: 'max-chars' },
        ],
    },
    {
        type: 'TextArea',
        label: 'Text area',
        group: 'input',
        capturesInput: true,
        sinceVersion: '1.0',
        props: [
            { name: 'name', required: true },
            { name: 'label', required: true },
            { name: 'required' },
            { name: 'helper-text' },
            { name: 'max-length' },
        ],
    },
    // ── Choice / pickers ─────────────────────────────────────────────────────
    {
        type: 'RadioButtonsGroup',
        label: 'Radio buttons',
        group: 'choice',
        capturesInput: true,
        sinceVersion: '1.0',
        props: [
            { name: 'name', required: true },
            { name: 'label', required: true },
            { name: 'data-source', required: true, hint: 'Array of { id, title, description? }' },
            { name: 'required' },
            { name: 'init-value' },
            { name: 'media-size' },
        ],
    },
    {
        type: 'CheckboxGroup',
        label: 'Checkboxes',
        group: 'choice',
        capturesInput: true,
        sinceVersion: '1.0',
        props: [
            { name: 'name', required: true },
            { name: 'label', required: true },
            { name: 'data-source', required: true, hint: 'Array of { id, title, description? }' },
            { name: 'min-selected-items' },
            { name: 'max-selected-items' },
            { name: 'required' },
            { name: 'init-value' },
        ],
    },
    {
        type: 'Dropdown',
        label: 'Dropdown',
        group: 'choice',
        capturesInput: true,
        sinceVersion: '1.0',
        props: [
            { name: 'name', required: true },
            { name: 'label', required: true },
            { name: 'data-source', required: true, hint: 'Array of { id, title } (1–200 items)' },
            { name: 'required' },
            { name: 'init-value' },
        ],
    },
    {
        type: 'ChipsSelector',
        label: 'Chips selector',
        group: 'choice',
        capturesInput: true,
        sinceVersion: '6.3',
        props: [
            { name: 'name', required: true },
            { name: 'label', required: true },
            { name: 'data-source', required: true, hint: 'Array of { id, title }' },
            { name: 'min-selected-items' },
            { name: 'max-selected-items' },
        ],
    },
    {
        type: 'DatePicker',
        label: 'Date picker',
        group: 'choice',
        capturesInput: true,
        sinceVersion: '1.0',
        props: [
            { name: 'name', required: true },
            { name: 'label', required: true },
            { name: 'min-date' },
            { name: 'max-date' },
            { name: 'unavailable-dates' },
        ],
    },
    {
        type: 'CalendarPicker',
        label: 'Calendar',
        group: 'choice',
        capturesInput: true,
        sinceVersion: '6.0',
        props: [
            { name: 'name', required: true },
            { name: 'label', required: true, hint: 'Object with start/end labels in range mode' },
            { name: 'mode', required: true, hint: 'single | range' },
            { name: 'min-date' },
            { name: 'max-date' },
            { name: 'include-days' },
            { name: 'min-days' },
            { name: 'max-days' },
        ],
    },
    {
        type: 'OptIn',
        label: 'Opt-in',
        group: 'choice',
        capturesInput: true,
        sinceVersion: '3.0',
        maxPerScreen: 5,
        props: [
            { name: 'name', required: true },
            { name: 'label', required: true },
            { name: 'required' },
            { name: 'on-click-action' },
        ],
    },
    // ── Media upload ─────────────────────────────────────────────────────────
    {
        type: 'PhotoPicker',
        label: 'Photo upload',
        group: 'media',
        capturesInput: true,
        sinceVersion: '4.0',
        props: [
            { name: 'name', required: true },
            { name: 'label', required: true },
            { name: 'description' },
            { name: 'photo-source', hint: 'camera_gallery | camera | gallery' },
            { name: 'min-uploaded-photos' },
            { name: 'max-uploaded-photos' },
            { name: 'max-file-size-kb' },
        ],
    },
    {
        type: 'DocumentPicker',
        label: 'Document upload',
        group: 'media',
        capturesInput: true,
        sinceVersion: '4.0',
        props: [
            { name: 'name', required: true },
            { name: 'label', required: true },
            { name: 'description' },
            { name: 'allowed-mime-types', hint: 'Array of MIME types' },
            { name: 'min-uploaded-documents' },
            { name: 'max-uploaded-documents' },
            { name: 'max-file-size-kb' },
        ],
    },
    // ── Navigation / actions ─────────────────────────────────────────────────
    {
        type: 'EmbeddedLink',
        label: 'Link',
        group: 'navigation',
        capturesInput: false,
        sinceVersion: '1.0',
        maxPerScreen: 2,
        props: [
            { name: 'text', required: true },
            { name: 'on-click-action', required: true, hint: 'navigate | data_exchange | open_url' },
        ],
    },
    {
        type: 'NavigationList',
        label: 'Navigation list',
        group: 'navigation',
        capturesInput: false,
        sinceVersion: '6.2',
        maxPerScreen: 2,
        props: [
            { name: 'name' },
            {
                name: 'list-items',
                required: true,
                hint: 'Array of { id, main-content, on-click-action }',
            },
            { name: 'on-click-action' },
        ],
    },
    {
        type: 'Footer',
        label: 'Footer button',
        group: 'navigation',
        capturesInput: false,
        sinceVersion: '1.0',
        maxPerScreen: 1,
        props: [
            { name: 'label', required: true, hint: 'Button label' },
            { name: 'on-click-action', required: true, hint: 'complete | navigate | data_exchange' },
            { name: 'left-caption' },
            { name: 'center-caption' },
            { name: 'right-caption' },
        ],
    },
];
/** Fast membership test for the validator. */
export const FLOW_COMPONENT_TYPES = new Set(FLOW_COMPONENTS.map((c) => c.type));
/** Component types that contribute a value to the submission (need a `name`). */
export const FLOW_INPUT_COMPONENT_TYPES = new Set(FLOW_COMPONENTS.filter((c) => c.capturesInput).map((c) => c.type));
export function getFlowComponent(type) {
    return FLOW_COMPONENTS.find((c) => c.type === type);
}
